import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Alert, Grid } from 'antd';
import {
  MailOutlined, LockOutlined, CheckCircleOutlined,
  SafetyCertificateOutlined, BarChartOutlined, TeamOutlined,
  SendOutlined, KeyOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const { useBreakpoint } = Grid;

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://sesmailbox.com';
// In dev with the test key, captcha always passes — no widget interaction needed
const captchaRequired = HCAPTCHA_SITE_KEY !== '10000000-ffff-ffff-ffff-000000000001';

/* ── Right hero panel ── */
const HeroPanel = () => (
  <div style={{
    flex: 1,
    background: 'linear-gradient(150deg, var(--brand-ink) 0%, oklch(0.32 0.12 265) 55%, oklch(0.28 0.15 263) 100%)',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '60px 56px', color: '#fff',
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 13,
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 32,
    }}>
      <img src="/icon.png" alt="" style={{ width: 30, height: 30, borderRadius: 7, display: 'block' }} />
    </div>

    <div style={{ fontSize: 32, fontWeight: 750, letterSpacing: '-0.04em', lineHeight: 1.15, marginBottom: 16, maxWidth: 340 }}>
      Send smarter<br />with SES Mailbox
    </div>

    <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', marginBottom: 48, maxWidth: 360, lineHeight: 1.6 }}>
      Professional email marketing built on AWS SES. Full deliverability control, your own sending infrastructure.
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {[
        { icon: <SendOutlined />,             text: 'Send campaigns to thousands in seconds' },
        { icon: <BarChartOutlined />,          text: 'Real-time open & click analytics' },
        { icon: <SafetyCertificateOutlined />, text: 'SPF · DKIM · DMARC deliverability tools' },
        { icon: <TeamOutlined />,              text: 'Smart suppression & bounce handling' },
      ].map(({ icon, text }) => (
        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: 'rgba(255,255,255,0.8)', flexShrink: 0,
          }}>
            {icon}
          </div>
          <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>{text}</span>
        </div>
      ))}
    </div>

    <div style={{
      marginTop: 56, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.1)',
      fontSize: 12.5, color: 'rgba(255,255,255,0.35)',
    }}>
      © {new Date().getFullYear()} SES Mailbox · The dashboard for AWS SES
    </div>
  </div>
);

/* ── Step 1: email + password + captcha ── */
const CredentialsForm = ({ onOtpSent, onForgot }) => {
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const captchaRef = useRef(null);

  const handleSubmit = async (values) => {
    if (captchaRequired && !captchaToken) {
      setCaptchaError(true);
      return;
    }
    setLoading(true); setError(''); setCaptchaError(false);
    try {
      const res = await axios.post('/api/auth/login', {
        email:         values.email,
        password:      values.password,
        captcha_token: captchaToken || '',
      });
      if (res.data.token) {
        // OTP disabled — backend returned token directly
        localStorage.setItem('token', res.data.token);
        if (res.data.name)  localStorage.setItem('userName',  res.data.name);
        if (res.data.email) localStorage.setItem('userEmail', res.data.email);
        window.location.href = '/';
      } else {
        // OTP enabled — move to verification step
        onOtpSent(values.email);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password.');
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <Alert message={error} type="error" showIcon closable
          onClose={() => setError('')}
          style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }}
        />
      )}
      <Form onFinish={handleSubmit} layout="vertical" size="large">
        <Form.Item
          name="email" label="Email address"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
        >
          <Input
            prefix={<MailOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="you@example.com"
            style={{ borderRadius: 'var(--r-md)', height: 44 }}
          />
        </Form.Item>

        <Form.Item
          name="password" label="Password"
          rules={[{ required: true, message: 'Enter your password' }]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="••••••••"
            style={{ borderRadius: 'var(--r-md)', height: 44 }}
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: -12, marginBottom: 20 }}>
          <button type="button" onClick={onForgot} style={{
            background: 'none', border: 'none',
            color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500,
          }}>
            Forgot password?
          </button>
        </div>

        {/* hCaptcha */}
        <div style={{ marginBottom: 20 }}>
          <HCaptcha
            ref={captchaRef}
            sitekey={HCAPTCHA_SITE_KEY}
            onVerify={(token) => { setCaptchaToken(token); setCaptchaError(false); }}
            onExpire={() => setCaptchaToken('')}
            theme="light"
            size="normal"
          />
          {captchaError && (
            <div style={{ color: 'var(--danger, #dc2626)', fontSize: 12, marginTop: 4 }}>
              Please complete the CAPTCHA
            </div>
          )}
        </div>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary" htmlType="submit" block loading={loading}
            style={{ height: 44, fontWeight: 600, borderRadius: 'var(--r-md)', fontSize: 14 }}
          >
            Continue
          </Button>
        </Form.Item>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-3)' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
            Create account
          </Link>
        </div>
      </Form>
    </>
  );
};

/* ── Step 2: OTP verification ── */
const OtpForm = ({ email, onSuccess, onBack }) => {
  const [otp,      setOtp]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const [resending, setResending] = useState(false);
  const [error,    setError]    = useState('');
  const [resent,   setResent]   = useState(false);
  const navigate = useNavigate();

  // Mask email for display: e.g. jo***@example.com
  const maskedEmail = email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(Math.max(3, b.length)) + c);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.trim().length !== 6) { setError('Enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post('/api/auth/verify-otp', { email, otp: otp.trim() });
      localStorage.setItem('token', res.data.token);
      if (res.data.name)  localStorage.setItem('userName',  res.data.name);
      if (res.data.email) localStorage.setItem('userEmail', res.data.email);
      onSuccess();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true); setError(''); setResent(false);
    try {
      // Re-POST login without captcha (backend skips captcha if HCAPTCHA_SECRET not set in dev,
      // for resend we don't have a fresh captcha token — instruct user to go back if needed)
      await axios.post('/api/auth/login', { email, password: '__resend__', captcha_token: '' });
    } catch {
      // Expected to fail (wrong password) — but OTP is re-sent on valid credentials
      // So we direct user to go back and re-enter password for a fresh code
    }
    // Actually, just tell them to go back and sign in again for a fresh code
    setError('To get a new code, go back and sign in again.');
    setResending(false);
  };

  return (
    <>
      {/* Icon */}
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: 'linear-gradient(135deg, #2e6bf5, #0a40d9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
      }}>
        <KeyOutlined style={{ fontSize: 26, color: '#fff' }} />
      </div>

      <div style={{ marginBottom: 8, fontWeight: 650, fontSize: 20, letterSpacing: '-0.03em', color: 'var(--text)' }}>
        Check your email
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--text-3)', marginBottom: 28, lineHeight: 1.55 }}>
        We sent a 6-digit code to <strong style={{ color: 'var(--text-2)' }}>{maskedEmail}</strong>.
        Enter it below to sign in. The code expires in 10 minutes.
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable
          onClose={() => setError('')}
          style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }}
        />
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 20 }}>
          <Input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            size="large"
            autoFocus
            style={{
              textAlign: 'center',
              letterSpacing: '10px',
              fontSize: 28,
              fontWeight: 700,
              fontFamily: 'monospace',
              height: 60,
              borderRadius: 'var(--r-md)',
              borderColor: otp.length === 6 ? 'var(--brand-primary)' : undefined,
            }}
          />
        </div>

        <Button
          type="primary" htmlType="submit" block
          loading={loading}
          disabled={otp.length !== 6}
          style={{ height: 44, fontWeight: 600, borderRadius: 'var(--r-md)', fontSize: 14, marginBottom: 12 }}
        >
          Verify & Sign in
        </Button>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <button type="button" onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-3)', fontSize: 13,
        }}>
          ← Back
        </button>
        <button type="button" onClick={() => onBack()} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--brand-primary)', fontSize: 13, fontWeight: 500,
        }}>
          Resend code
        </button>
      </div>
    </>
  );
};

/* ── Forgot password form ── */
const ForgotPasswordForm = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async ({ email }) => {
    setLoading(true); setError('');
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 16 }}>
        <CheckCircleOutlined style={{ fontSize: 44, color: 'var(--ok)', marginBottom: 16, display: 'block' }} />
        <div style={{ color: 'var(--text-2)', fontSize: 14.5, marginBottom: 24, lineHeight: 1.5 }}>
          If that email exists, a password reset link has been sent. Check your inbox.
        </div>
        <Button type="default" onClick={onBack} style={{ borderRadius: 'var(--r-md)' }}>
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 20, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
        Enter your email and we'll send you a reset link.
      </div>
      {error && (
        <Alert message={error} type="error" showIcon closable
          onClose={() => setError('')}
          style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }}
        />
      )}
      <Form onFinish={handleSubmit} layout="vertical" size="large">
        <Form.Item
          name="email" label="Email address"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
        >
          <Input
            prefix={<MailOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="you@example.com"
            style={{ borderRadius: 'var(--r-md)', height: 44 }}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={loading}
            style={{ height: 44, fontWeight: 600, borderRadius: 'var(--r-md)' }}>
            Send Reset Link
          </Button>
        </Form.Item>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button type="button" onClick={onBack} style={{
            background: 'none', border: 'none',
            color: 'var(--text-3)', cursor: 'pointer', fontSize: 13,
          }}>
            Back to Sign in
          </button>
        </div>
      </Form>
    </>
  );
};

/* ── Page shell ── */
const STEP = { CREDENTIALS: 'credentials', OTP: 'otp', FORGOT: 'forgot' };

const Login = () => {
  const [step,  setStep]  = useState(STEP.CREDENTIALS);
  const [email, setEmail] = useState('');
  const screens  = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  // Redirect already-authenticated users to dashboard
  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const headings = {
    [STEP.CREDENTIALS]: { title: 'Welcome back',    sub: 'Sign in to your account to continue' },
    [STEP.OTP]:         { title: 'Verify your identity', sub: null },
    [STEP.FORGOT]:      { title: 'Reset password',  sub: null },
  };

  const { title, sub } = headings[step];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>

      {/* Left: form */}
      <div style={{
        width: isMobile ? '100%' : 480,
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '40px 24px' : '48px',
        background: 'var(--surface)',
        borderRight: isMobile ? 'none' : '1px solid var(--border)',
      }}>
        <div style={{ width: '100%', maxWidth: 360 }}>

          <a href={MARKETING_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 500, color: 'var(--text-3)', textDecoration: 'none', marginBottom: 22 }}>
            ← Back to home
          </a>

          {/* Logo */}
          <div style={{ marginBottom: 36 }}>
            <a href={MARKETING_URL} title="Back to sesmailbox.com" aria-label="Back to SES Mailbox home">
              <img src="/logo.png" alt="SES Mailbox" style={{ height: 56, width: 'auto', display: 'block' }} />
            </a>
          </div>

          {step !== STEP.OTP && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 650, fontSize: 22, letterSpacing: '-0.03em', color: 'var(--text)', marginBottom: 4 }}>
                {title}
              </div>
              {sub && <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>{sub}</div>}
            </div>
          )}

          {step === STEP.CREDENTIALS && (
            <CredentialsForm
              onOtpSent={(e) => { setEmail(e); setStep(STEP.OTP); }}
              onForgot={() => setStep(STEP.FORGOT)}
            />
          )}

          {step === STEP.OTP && (
            <OtpForm
              email={email}
              onSuccess={() => {}}
              onBack={() => setStep(STEP.CREDENTIALS)}
            />
          )}

          {step === STEP.FORGOT && (
            <ForgotPasswordForm onBack={() => setStep(STEP.CREDENTIALS)} />
          )}

        </div>
      </div>

      {/* Right: hero (desktop only) */}
      {!isMobile && <HeroPanel />}
    </div>
  );
};

export default Login;
